import * as React from 'react'
import { create } from 'zustand'
import { createJSONStorage, devtools, persist } from 'zustand/middleware'
import { useShallow } from 'zustand/shallow'

import { ReportType } from '@/app/constants/reportType'

/**
 * Zaznaczenie raportow do wydruku grupowego, trzymane miedzy sesjami -
 * odpowiednik SelectedReportsConfiguration z WPF (zapis w OnDeactivated,
 * odczyt w OnActivated). Kazda lista (wszystkie/ulubione/do wyslania) ma wlasne
 * zaznaczenie, bo pokazuje inny zestaw raportow. Zaznaczenie z innej firmy jest
 * nieaktualne.
 */
export type ReportSelectionScope = 'list' | 'favorites' | 'shipping'

type State = {
  selectionsByScope: Record<string, ReportType[]>
  ownerCompanyId: string | undefined
}

type Actions = {
  toggleReport: (scope: ReportSelectionScope, report: ReportType) => void
  setReports: (
    scope: ReportSelectionScope,
    reports: ReportType[],
    select: boolean,
  ) => void
  clearReports: (scope: ReportSelectionScope) => void
  syncCompany: (companyId: string) => void
}

const EMPTY: ReportType[] = []

export const useSelectedReportsStore = create<State & Actions>()(
  devtools(
    persist(
      (set) => ({
        selectionsByScope: {},
        ownerCompanyId: undefined,

        toggleReport: (scope, report) =>
          set(
            (s) => {
              const current = s.selectionsByScope[scope] ?? EMPTY
              const next = current.includes(report)
                ? current.filter((r) => r !== report)
                : [...current, report]
              return {
                selectionsByScope: { ...s.selectionsByScope, [scope]: next },
              }
            },
            false,
            'toggleReport',
          ),

        setReports: (scope, reports, select) =>
          set(
            (s) => {
              if (reports.length === 0) return {}
              const current = s.selectionsByScope[scope] ?? EMPTY
              const changed = new Set(reports)
              const rest = current.filter((r) => !changed.has(r))
              return {
                selectionsByScope: {
                  ...s.selectionsByScope,
                  [scope]: select ? [...rest, ...reports] : rest,
                },
              }
            },
            false,
            'setReports',
          ),

        clearReports: (scope) =>
          set(
            (s) => ({
              selectionsByScope: { ...s.selectionsByScope, [scope]: [] },
            }),
            false,
            'clearReports',
          ),

        syncCompany: (companyId) =>
          set(
            (s) =>
              s.ownerCompanyId === companyId
                ? {}
                : { ownerCompanyId: companyId, selectionsByScope: {} },
            false,
            'syncCompany',
          ),
      }),
      {
        name: 'printout-selected-reports',
        storage: createJSONStorage(() => localStorage),
        partialize: (s) => ({
          selectionsByScope: s.selectionsByScope,
          ownerCompanyId: s.ownerCompanyId,
        }),
      },
    ),
    { name: 'EasyTacho', store: 'printout-selected-reports' },
  ),
)

export const useSelectedReports = (scope: ReportSelectionScope) => {
  const { selections, toggleReport, setReports, clearReports, syncCompany } =
    useSelectedReportsStore(
      useShallow((s) => ({
        selections: s.selectionsByScope[scope] ?? EMPTY,
        toggleReport: s.toggleReport,
        setReports: s.setReports,
        clearReports: s.clearReports,
        syncCompany: s.syncCompany,
      })),
    )

  return {
    selectedReports: selections,
    toggleReport: React.useCallback(
      (report: ReportType) => toggleReport(scope, report),
      [toggleReport, scope],
    ),
    setReports: React.useCallback(
      (reports: ReportType[], select: boolean) =>
        setReports(scope, reports, select),
      [setReports, scope],
    ),
    clearReports: React.useCallback(
      () => clearReports(scope),
      [clearReports, scope],
    ),
    syncCompany,
  }
}
